import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, deleteDoc, doc, query, where, orderBy, getDoc, limit } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { FaEye, FaEdit, FaTrash, FaRoute, FaBus, FaClock, FaRoad } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const OptimizedRoutes = () => {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [routeToDelete, setRouteToDelete] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserRole();
  }, []);

  const fetchUserRole = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      // First, try to get user document by document ID (matching auth UID)
      let userDoc = await getDoc(doc(db, 'users', user.uid));
      
      // If document doesn't exist with that ID, try querying by uid field
      if (!userDoc.exists()) {
        const usersRef = collection(db, 'users');
        const uidQuery = query(usersRef, where('uid', '==', user.uid), limit(1));
        const querySnapshot = await getDocs(uidQuery);
        
        if (!querySnapshot.empty) {
          // Found user document with uid field matching auth UID
          userDoc = querySnapshot.docs[0];
        }
      }
      
      if (userDoc.exists()) {
        const role = userDoc.data().role;
        setUserRole(role);
        setCurrentUserId(user.uid);
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  };

  const fetchRoutes = useCallback(async () => {
    try {
      setLoading(true);
      const routesRef = collection(db, 'optimizedRoutes');
      const q = query(routesRef, where('status', '==', 'active'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      let routesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));

      // Filter routes for drivers/assistants - only show routes for their assigned buses
      if (userRole === 'driver' || userRole === 'assistant') {
        // First, fetch all vehicles to find which buses are assigned to the user
        const vehiclesRef = collection(db, 'vehicles');
        const vehiclesSnapshot = await getDocs(vehiclesRef);
        const assignedBusIds = new Set();
        
        vehiclesSnapshot.docs.forEach(vehicleDoc => {
          const vehicle = vehicleDoc.data();
          if ((vehicle.assignedDriver && vehicle.assignedDriver.id === currentUserId) ||
              (vehicle.assignedAssistant && vehicle.assignedAssistant.id === currentUserId)) {
            assignedBusIds.add(vehicleDoc.id);
          }
        });

        // Filter routes to only show those assigned to the user's buses
        routesData = routesData.filter(route => {
          return route.busId && assignedBusIds.has(route.busId);
        });
      }
      
      setRoutes(routesData);
    } catch (error) {
      console.error('Error fetching routes:', error);
      setError('Failed to load routes');
    } finally {
      setLoading(false);
    }
  }, [userRole, currentUserId]);

  useEffect(() => {
    if (userRole !== null) {
      fetchRoutes();
    }
  }, [userRole, currentUserId, fetchRoutes]);

  const handleViewRoute = (route) => {
    setSelectedRoute(route);
    setShowViewModal(true);
  };

  const handleEditRoute = (route) => {
    // Navigate to routes engine with route data encoded in URL parameters
    const routeData = encodeURIComponent(JSON.stringify(route));
    navigate(`/routes/engine?routeData=${routeData}`);
  };

  const handleDeleteRoute = (route) => {
    setRouteToDelete(route);
    setShowDeleteModal(true);
  };

  const confirmDeleteRoute = async () => {
    if (!routeToDelete) return;

    try {
      await deleteDoc(doc(db, 'optimizedRoutes', routeToDelete.id));
      setRoutes(routes.filter(route => route.id !== routeToDelete.id));
      setShowDeleteModal(false);
      setRouteToDelete(null);
      setError('Route deleted successfully!');
      
      setTimeout(() => {
        setError('');
      }, 3000);
    } catch (error) {
      console.error('Error deleting route:', error);
      setError('Failed to delete route');
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    return duration;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00E0E0] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-700">Loading Routes...</h2>
          <p className="text-gray-500 mt-2">Please wait while we fetch your optimized routes</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-[#f6f7fb] pt-8 pb-8">
      {/* Header */}
      <div className="px-8 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-black">Optimized Routes</h1>
            <p className="text-gray-600 mt-2">View and manage your saved optimized routes</p>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={fetchRoutes}
              className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-[#00E0E0] text-white font-semibold hover:bg-[#00B8B8] transition-colors"
            >
              <FaRoute />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Error/Success Message */}
      {error && (
        <div className="px-8 mb-4">
          <div className={`p-4 rounded-lg ${
            error.includes('successfully') 
              ? 'bg-green-100 text-green-700 border border-green-200' 
              : 'bg-red-100 text-red-700 border border-red-200'
          }`}>
            {error}
          </div>
        </div>
      )}

      {/* Routes List */}
      <div className="px-8">
        {routes.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <FaRoute className="text-6xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Optimized Routes Found</h3>
            <p className="text-gray-500 mb-4">
              {userRole === 'driver' || userRole === 'assistant' 
                ? "You don't have any assigned routes yet."
                : "You haven't saved any optimized routes yet. Create routes in the Routes Engine and save them to see them here."}
            </p>
            {(userRole !== 'driver' && userRole !== 'assistant') && (
              <button
                onClick={() => navigate('/routes/engine')}
                className="px-6 py-2 bg-[#00E0E0] text-white font-semibold rounded-lg hover:bg-[#00B8B8] transition-colors"
              >
                Go to Routes Engine
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {routes.map((route) => (
              <div key={route.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <h3 className="text-xl font-semibold text-gray-900">{route.name}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        route.tripType === 'AM' 
                          ? 'bg-blue-100 text-blue-800' 
                          : route.tripType === 'PM'
                          ? 'bg-orange-100 text-orange-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {route.tripType === 'AM' ? 'School Trip' : route.tripType === 'PM' ? 'Home Trip' : 'Field Trip'}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="flex items-center space-x-2">
                        <FaRoute className="text-[#00E0E0]" />
                        <div>
                          <p className="text-sm text-gray-500">Roster</p>
                          <p className="font-medium">{route.rosterName}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <FaBus className="text-[#00E0E0]" />
                        <div>
                          <p className="text-sm text-gray-500">Bus</p>
                          <p className="font-medium">
                            {route.busDetails ? `Fleet #${route.busDetails.fleetNumber}` : 'N/A'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <FaClock className="text-[#00E0E0]" />
                        <div>
                          <p className="text-sm text-gray-500">Duration</p>
                          <p className="font-medium">{formatDuration(route.routeMetrics?.duration)}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <FaRoad className="text-[#00E0E0]" />
                        <div>
                          <p className="text-sm text-gray-500">Distance</p>
                          <p className="font-medium">{route.routeMetrics?.distance || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center space-x-4">
                        <span>Students: {route.studentSequence?.length || 0}</span>
                        <span>Created: {formatDate(route.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleViewRoute(route)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="View Route"
                    >
                      <FaEye />
                    </button>
                    {(userRole !== 'driver' && userRole !== 'assistant') && (
                      <>
                        <button
                          onClick={() => handleEditRoute(route)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Edit Route"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDeleteRoute(route)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Route"
                        >
                          <FaTrash />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* View Route Modal */}
      {showViewModal && selectedRoute && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Route Details: {selectedRoute.name}</h2>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ×
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold mb-3">Route Information</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Roster</p>
                    <p className="font-medium">{selectedRoute.rosterName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Trip Type</p>
                    <p className="font-medium">
                      {selectedRoute.tripType === 'AM' ? 'School Trip' : selectedRoute.tripType === 'PM' ? 'Home Trip' : 'Field Trip'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Time</p>
                    <p className="font-medium">{selectedRoute.time || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Bus</p>
                    <p className="font-medium">
                      {selectedRoute.busDetails ? 
                        `Fleet #${selectedRoute.busDetails.fleetNumber} - ${selectedRoute.busDetails.registrationNumber}` : 
                        'N/A'
                      }
                    </p>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-3">Route Metrics</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Total Duration</p>
                    <p className="font-medium">{formatDuration(selectedRoute.routeMetrics?.duration)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Distance</p>
                    <p className="font-medium">{selectedRoute.routeMetrics?.distance || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Driving Time</p>
                    <p className="font-medium">
                      {selectedRoute.routeMetrics?.totalDuration ? 
                        `${Math.floor(selectedRoute.routeMetrics.totalDuration / 3600)}h ${Math.floor((selectedRoute.routeMetrics.totalDuration % 3600) / 60)}m` : 
                        'N/A'
                      }
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Wait Time</p>
                    <p className="font-medium">
                      {selectedRoute.routeMetrics?.totalWaitTime ? 
                        `${Math.floor(selectedRoute.routeMetrics.totalWaitTime / 3600)}h ${Math.floor((selectedRoute.routeMetrics.totalWaitTime % 3600) / 60)}m` : 
                        'N/A'
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-3">Student Sequence</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {selectedRoute.studentSequence?.map((student, index) => (
                    <div key={student.id} className="flex items-center space-x-3 p-2 bg-white rounded border">
                      <div className="w-6 h-6 bg-[#00E0E0] text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {student.sequence}
                      </div>
                      <div>
                        <p className="font-medium">{student.firstName} {student.lastName}</p>
                        <p className="text-xs text-gray-500">
                          {student.location ? `${student.location.lat.toFixed(4)}, ${student.location.lng.toFixed(4)}` : 'No location'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && routeToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Delete Route</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{routeToDelete.name}"? This action cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setRouteToDelete(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteRoute}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OptimizedRoutes; 